import React, { createContext, useContext, useState, useEffect } from 'react';
import ApiService from '../services/api';
import { getSession } from '../utils/authUtils';

// Role IDs - Only role_id 9 has admin access
export const ROLES = {
    ADMIN: 9,        // Admin role - only role with admin dashboard access
    SUPER_ADMIN: 9,  // Alias for backwards compatibility
    EMPLOYEE: 4,     // Generic employee role (includes BDM Sales)
    BDM_OUTSIDE: 4,  // BDM Sales (outside) uses role_id 4
    BDM_INSIDE: 4,   // BDM Sales (inside) also uses role_id 4
    CRO: 2,          // CRO uses role_id 2
};

// Define which menu items each role can see
export const ROLE_PERMISSIONS = {
    9: 'all', // Admin (role_id 9) sees everything
    [ROLES.ADMIN]: 'all', // Alias for role_id 9
    // Other roles have limited access
    2: [
        'users', 'clients', 'clientScores',
        'leads', 'meeting', 'festivals', 'attendance', 'photos', 'tasks',
        'holidays', 'gmbs', 'localCitation', 'followups', 'welcomeCalls'
    ],
    // BDM Sales and CRO (role_id 4) - Sales team focused view (no dashboard)
    4: [
        'clients', 'leads', 'meeting', 'tasks', 'holidays', 'followups', 'welcomeCalls'
    ],
};

// Keywords to identify BDM Sales (inside) / CRO roles (case insensitive)
const INSIDE_SALES_KEYWORDS = ['inside', 'cro', 'tele', 'telecaller'];

const AdminRoleContext = createContext();

export const AdminRoleProvider = ({ children }) => {
    const [roleId, setRoleId] = useState(null);
    const [employeeData, setEmployeeData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sessionToken, setSessionToken] = useState(() => {
        const session = getSession();
        return session?.token || session?.user?.token || null;
    });

    useEffect(() => {
        const handleSessionUpdate = () => {
            const session = getSession();
            setSessionToken(session?.token || session?.user?.token || null);
        };
        window.addEventListener('sessionChanged', handleSessionUpdate);
        return () => window.removeEventListener('sessionChanged', handleSessionUpdate);
    }, []);

    useEffect(() => {
        if (!sessionToken) {
            setRoleId(null); // No session, no role
            setEmployeeData(null);
            setLoading(false);
            return;
        }

        const fetchEmployeeRole = async () => {
            setLoading(true);
            try {
                // Get user ID from localStorage
                const userData = localStorage.getItem('userData') || localStorage.getItem('user');
                let userId = null;

                if (userData) {
                    try {
                        const parsed = JSON.parse(userData);
                        userId = parsed?.id || parsed?.user_id || parsed?.userId;
                    } catch (e) {
                        console.warn('Failed to parse user data');
                    }
                }

                // Also try direct localStorage values
                if (!userId) {
                    userId = localStorage.getItem('userId') || localStorage.getItem('user_id');
                }

                if (userId) {
                    const res = await ApiService.get(`/admin/employee/get-employee-by-user/${userId}`);
                    const emp = res?.data?.data || res?.data || res;

                    if (emp && emp.id) {
                        setEmployeeData({
                            id: emp.id,
                            department: emp.department,
                            designation: emp.designation,
                            name: [emp.first_name, emp.last_name].filter(Boolean).join(' '),
                        });
                        setRoleId(emp.role_id || emp.role?.id || null);
                    } else {
                        setRoleId(null); // No employee found
                    }
                } else {
                    setRoleId(null); // No user ID
                }
            } catch (error) {
                console.warn('Failed to fetch employee role:', error);
                setRoleId(null); // Error fallback
            } finally {
                setLoading(false);
            }
        };

        fetchEmployeeRole();
    }, [sessionToken]);

    // Check if the current role can access a specific menu item
    const canAccess = (menuItemId) => {
        if (loading || roleId === null) return true; // Show all while loading

        const permissions = ROLE_PERMISSIONS[roleId];
        if (permissions === 'all') return true; // Super Admin
        if (Array.isArray(permissions)) return permissions.includes(menuItemId);
        return false;
    };

    // Get role name based on department/designation
    const getRoleName = () => {
        if (roleId === ROLES.ADMIN) return 'Admin';
        if (roleId === 2) return 'CRO';
        if (roleId === 4) {
            // Determine BDM Sales (outside/inside) or CRO based on department/designation
            const dept = (employeeData?.department || '').toLowerCase();
            const designation = (employeeData?.designation || '').toLowerCase();
            const combined = `${dept} ${designation}`;

            // Check for CRO
            if (combined.includes('cro')) {
                return 'CRO';
            }
            // Check if matches any inside sales keywords
            if (INSIDE_SALES_KEYWORDS.some(keyword => combined.includes(keyword))) {
                return 'BDM Sales (inside)';
            }
            // Default to BDM Sales (outside) for all other role_id=4 employees
            return 'BDM Sales (outside)';
        }
        return 'Unknown';
    };

    // Get role filter for API calls (bdm_outside, bdm_inside, cro, or null for all)
    const getRoleFilter = () => {
        if (roleId === ROLES.ADMIN) return null; // Admin (role_id 9) sees all
        if (roleId !== 4) return null; // Non-BDM roles see all
        const dept = (employeeData?.department || '').toLowerCase();
        const designation = (employeeData?.designation || '').toLowerCase();
        const combined = `${dept} ${designation}`;

        // Check for CRO
        if (combined.includes('cro')) {
            return 'cro';
        }
        // Check if matches any inside sales keywords
        if (INSIDE_SALES_KEYWORDS.some(keyword => combined.includes(keyword))) {
            return 'bdm_inside';
        }
        // Default to BDM Sales (outside) for all other role_id=4 employees
        return 'bdm_outside';
    };

    // Get query params for role-based filtering in API calls
    const getFilterParams = () => {
        if (roleId === ROLES.ADMIN) {
            return ''; // No filtering for admin (role_id 9)
        }
        if (employeeData?.id) {
            const roleFilter = getRoleFilter();
            return `&employee_id=${employeeData.id}${roleFilter ? `&role_filter=${roleFilter}` : ''}`;
        }
        return '';
    };

    return (
        <AdminRoleContext.Provider value={{
            roleId,
            employeeData,
            employeeId: employeeData?.id || null,
            loading,
            canAccess,
            getRoleName,
            getRoleFilter,
            getFilterParams,
            isSuperAdmin: roleId === ROLES.ADMIN, // role_id 9 is now the super admin
            isAdmin: roleId === ROLES.ADMIN,     // role_id 9
            isBDMOutside: roleId === 4 && getRoleFilter() === 'bdm_outside',
            isBDMInside: roleId === 4 && getRoleFilter() === 'bdm_inside',
            isCRO: roleId === 4 && getRoleFilter() === 'cro',
            // Legacy aliases for backwards compatibility
            isBDM: roleId === 4 && getRoleFilter() === 'bdm_outside',
            isTelly: roleId === 4 && (getRoleFilter() === 'bdm_inside' || getRoleFilter() === 'cro'),
            isSalesTeam: roleId === 4, // Any sales role (BDM outside/inside or CRO)
        }}>
            {children}
        </AdminRoleContext.Provider>
    );
};

export const useAdminRole = () => {
    const context = useContext(AdminRoleContext);
    if (!context) {
        throw new Error('useAdminRole must be used within an AdminRoleProvider');
    }
    return context;
};

export default AdminRoleContext;
